import React, { useMemo, useState } from "react"
import { Modal, Input } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy } from "@fortawesome/free-solid-svg-icons"

import type {
  BaseCheckDiagnostic,
  BaseCheckInstance,
  BaseCheckQuestion,
} from "types/dataChecks.types"
import {
  describeAlwaysAsked,
  explainExamplesUnavailable,
  formatCoverageLabel,
  formatExampleCaption,
  formatFieldOffPath,
  formatIteration,
  hasTabSpecCondition,
  summariseDiagnostics,
  TONE,
  toneForCoverage,
} from "features/DataChecks/components/baseCheckFormat"

interface QuestionDetailModalProps {
  open: boolean
  question: BaseCheckQuestion
  /** Report-level — false means the example lists are empty for a reason that is not "none found". */
  examplesAvailable: boolean
  /** Report-level diagnostics; they carry WHY the ids are unavailable. */
  reportDiagnostics: BaseCheckDiagnostic[]
  onClose: () => void
}

const Figure: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="text-right">
    <div className="text-[13px] font-medium text-slate-800">{value.toLocaleString()}</div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
)

const copy = (text: string): void => {
  void navigator.clipboard?.writeText(text)
}

/**
 * One question, shown all the way down: the condition tab specs see, every instance the routing
 * produced, every variable inside them, and who answered off path.
 *
 * This is the "show your work" view. Where the asked condition can be written out, it is
 * reproduced exactly as the engine wrote it — never paraphrased, never prettified — so a user can
 * read it and judge it themselves.
 *
 * @component
 */
const QuestionDetailModal: React.FC<QuestionDetailModalProps> = ({
  open,
  question,
  examplesAvailable,
  reportDiagnostics,
  onClose,
}) => {
  const [search, setSearch] = useState("")
  const query = search.trim().toLowerCase()

  const instances = useMemo((): BaseCheckInstance[] => {
    if (query === "") return question.instances
    return question.instances.flatMap((instance) => {
      const matchesInstance =
        instance.path.toLowerCase().includes(query) ||
        formatIteration(instance.iteration).toLowerCase().includes(query)
      if (matchesInstance) return [instance]
      const fields = instance.fields.filter(
        (field) =>
          field.path.toLowerCase().includes(query) ||
          field.type.toLowerCase().includes(query)
      )
      return fields.length > 0 ? [{ ...instance, fields }] : []
    })
  }, [question.instances, query])

  const diagnostics = summariseDiagnostics(question.diagnostics)

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      centered
      getContainer={() => document.body}
      title={`${question.label || question.external} — base check detail`}
    >
      {/* What we are looking at, and what tab specs can do with it. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[12px] text-slate-500">{question.external}</span>
        <span
          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${TONE[toneForCoverage(question.coverage)]}`}
        >
          {formatCoverageLabel(question.coverage)}
        </span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500">
          {question.kind === "data"
            ? "data variable — never cleaned"
            : question.kind === "hidden"
              ? "hidden question — checked and reported"
              : "question"}
        </span>
        {/* `cleanable` is a presentation choice that moves with include_hidden; it is never a
            measurement, so it is stated as an offer rather than as a finding. */}
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500">
          {question.cleanable ? "offered to cleaning" : "not offered to cleaning"}
        </span>
      </div>

      {/* GATE — branch on coverage, never on the expression's presence. */}
      <div className="mb-4 rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            The asked condition, as tab specs see it
          </span>
          {hasTabSpecCondition(question.coverage) && question.askedExpression && (
            <button
              type="button"
              onClick={() => copy(question.askedExpression ?? "")}
              aria-label="Copy the asked condition"
              className="dq-btn dq-btn--icon dq-btn--quiet"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          )}
        </div>
        {hasTabSpecCondition(question.coverage) && question.askedExpression ? (
          <pre className="m-0 max-h-40 overflow-auto whitespace-pre-wrap break-all px-3 py-2 font-mono text-[12px] text-slate-700">
            {question.askedExpression}
          </pre>
        ) : (
          <p className="m-0 px-3 py-2 text-[12px] leading-relaxed text-slate-500">
            This question&apos;s asked condition can&apos;t be shown to tab specs, so a table based
            on who was asked uses everyone who answered. That is about tables only: the check
            still walked the routing for every respondent, and the figures below are what it found.
          </p>
        )}
      </div>

      {/* Question-level marginals: every question is walked, so these are all measurements. */}
      <div className="mb-1 grid grid-cols-3 gap-3 rounded-lg border border-slate-200 p-3 sm:grid-cols-6">
        <Figure label="Expected" value={question.expected} />
        <Figure label="Not asked" value={question.notAsked} />
        <Figure label="Answered" value={question.observed} />
        <Figure label="Off path" value={question.offPath} />
        <Figure label="Asked, blank" value={question.missingOnPath} />
        <Figure label="Undetermined" value={question.undetermined} />
      </div>
      {/* GATE — the counts overlap: off path and blank are read cell by cell, so the same
          respondent can sit in more than one of them. Said once, here, so nobody adds them up. */}
      <p className="mb-4 mt-0 text-[11px] leading-snug text-slate-400">
        Answered, Off path and Asked, blank are read cell by cell, so one respondent can be
        undetermined on one cell and off path on another; don&apos;t add them up.
      </p>

      <Input.Search
        allowClear
        placeholder="Search instances and variables"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-3"
      />

      <div className="mb-4 overflow-hidden rounded border border-slate-200">
        <div className="grid grid-cols-[1fr_repeat(5,minmax(0,4.5rem))] gap-2 border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
          <span>Instance</span>
          <span className="text-right">Expected</span>
          <span className="text-right">Undet.</span>
          <span className="text-right">Off path</span>
          <span className="text-right">Blank</span>
          <span className="text-right">In data</span>
        </div>

        <div className="max-h-80 overflow-y-auto overflow-x-hidden [scrollbar-width:thin]">
          {instances.map((instance) => {
            const alwaysAsked = describeAlwaysAsked(instance)
            // An instance's own condition is worth showing only where it says something the
            // question's line above does not: a looped question's later iterations.
            const ownExpression =
              hasTabSpecCondition(instance.coverage) &&
              instance.askedExpression &&
              instance.askedExpression !== question.askedExpression
                ? instance.askedExpression
                : undefined
            return (
              <div
                key={instance.path}
                className="border-b border-slate-100 px-3 py-2 last:border-b-0"
              >
                <div className="grid grid-cols-[1fr_repeat(5,minmax(0,4.5rem))] items-baseline gap-2 text-[12px]">
                  <span className="min-w-0 break-all font-mono text-slate-800">
                    {instance.path}
                  </span>
                  <span className="text-right text-slate-700">
                    {instance.expected.toLocaleString()}
                  </span>
                  <span className="text-right text-slate-700">
                    {instance.undetermined.toLocaleString()}
                  </span>
                  <span className="text-right text-slate-700">
                    {instance.offPath.toLocaleString()}
                  </span>
                  <span className="text-right text-slate-700">
                    {instance.missingOnPath.toLocaleString()}
                  </span>
                  <span className="text-right text-[11px] text-slate-500">
                    {instance.masked ? "yes" : "no"}
                  </span>
                </div>

                {instance.iteration.length > 0 && (
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {formatIteration(instance.iteration)}
                  </div>
                )}
                {!hasTabSpecCondition(instance.coverage) && (
                  <div className="mt-0.5 text-[11px] text-sky-700">
                    {formatCoverageLabel(instance.coverage)}
                  </div>
                )}
                {ownExpression && (
                  <div className="mt-0.5 break-all font-mono text-[11px] text-slate-500">
                    {ownExpression}
                  </div>
                )}
                {!instance.masked && (
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    This instance has no field in the data, so nobody could have answered it here.
                  </div>
                )}
                {alwaysAsked && (
                  <div className="mt-0.5 text-[11px] text-slate-500">{alwaysAsked}</div>
                )}

                {instance.fields.length > 0 && (
                  <div className="mt-1.5 rounded border border-slate-100 bg-slate-50">
                    <div className="grid grid-cols-[1fr_3.5rem_4.5rem] gap-2 border-b border-slate-100 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                      <span>Variable</span>
                      <span>Type</span>
                      <span className="text-right">Off path</span>
                    </div>
                    {instance.fields.map((field) => (
                      <div
                        key={field.path}
                        className="grid grid-cols-[1fr_3.5rem_4.5rem] items-baseline gap-2 px-2 py-1 text-[11px]"
                      >
                        <span className="min-w-0 break-all font-mono text-slate-600">
                          {field.path}
                        </span>
                        <span className="font-mono text-slate-500">{field.type}</span>
                        <span
                          className={`text-right ${
                            field.countable ? "text-slate-700" : "text-slate-400"
                          }`}
                        >
                          {formatFieldOffPath(field)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}

          {instances.length === 0 && (
            <p className="m-0 px-3 py-3 text-[12px] text-slate-500">
              {question.instances.length === 0
                ? "No instances were produced for this question."
                : `Nothing here matches “${search}”.`}
            </p>
          )}
        </div>
      </div>

      {/* Who. An empty list is never left to speak for itself. */}
      <div className="mb-4 rounded border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Respondents with an off-path answer
          </span>
          {question.examples.length > 0 && (
            <button
              type="button"
              onClick={() => copy(question.examples.join("\n"))}
              aria-label="Copy respondent ids"
              className="dq-btn dq-btn--icon dq-btn--quiet"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          )}
        </div>
        {!examplesAvailable ? (
          <p className="m-0 px-3 py-2 text-[12px] leading-relaxed text-slate-500">
            {explainExamplesUnavailable(reportDiagnostics)}
          </p>
        ) : question.examples.length === 0 ? (
          <p className="m-0 px-3 py-2 text-[12px] text-slate-500">
            No respondent ids are listed for this question.
          </p>
        ) : (
          <div className="px-3 py-2">
            <p className="mb-1 mt-0 text-[11px] text-slate-500">
              {formatExampleCaption(question)}
            </p>
            <div className="max-h-24 overflow-y-auto break-all font-mono text-[11px] text-slate-600">
              {question.examples.join(", ")}
            </div>
          </div>
        )}
      </div>

      {diagnostics.length > 0 && (
        <div className="rounded border border-slate-200">
          <div className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            What the check said about this question
          </div>
          {diagnostics.map((diagnostic) => (
            <div key={diagnostic.code} className="px-3 py-1.5 text-[12px] text-slate-600">
              <span className="font-mono text-[11px] text-slate-400">{diagnostic.code}</span>
              {diagnostic.count > 1 && (
                <span className="ml-1 text-[11px] text-slate-400">×{diagnostic.count}</span>
              )}
              <div className="break-words">{diagnostic.message}</div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}

export default QuestionDetailModal
